import React from "react";
import { Check, Star } from "lucide-react";
import { Button } from "@/components/ui/button";

const pricingSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "JNN Homes Airbnb Property Management",
  provider: {
    "@type": "LocalBusiness",
    name: "JNN Homes",
    url: "https://jnnhomes.com",
    telephone: "+17038905425",
    areaServed: [
      "Fairfax, VA",
      "Alexandria, VA",
      "Arlington, VA",
      "Loudoun County, VA",
      "Prince William County, VA",
    ],
  },
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Airbnb Management Tiers",
    itemListElement: [
      {
        "@type": "Offer",
        name: "Essential — 15%",
        description:
          "Listing optimization, AI pricing, 24/7 guest messaging, check-in/out coordination, review management, revenue optimization, guest screening, calendar management.",
        price: "15",
        priceCurrency: "PERCENT",
      },
      {
        "@type": "Offer",
        name: "Full Service — 20%",
        description:
          "Everything in Essential plus cleaning & turnover, property inspection, inventory restocking, noise monitoring, emergency repair coordination, smart lock management, monthly owner reports.",
        price: "20",
        priceCurrency: "PERCENT",
      },
      {
        "@type": "Offer",
        name: "Premium Plus — 25%",
        description:
          "Everything in Tiers 1 & 2 plus dedicated account manager, custom revenue growth plan, professional photography, interior consulting, quarterly strategy calls, multi-property expansion planning.",
        price: "25",
        priceCurrency: "PERCENT",
      },
    ],
  },
};

const tiers = [
  {
    name: "Essential",
    fee: "15%",
    description:
      "Perfect for hands-on owners who need expert marketing and guest management.",
    features: [
      "Listing Optimization",
      "AI-Driven Dynamic Pricing",
      "24/7 Guest Messaging",
      "Check-In / Check-Out Coordination",
      "Review Management",
      "Market & Competitor Analysis",
      "Revenue Optimization",
      "Guest Screening",
      "Occupancy Strategy",
      "Calendar Management",
    ],
    highlighted: false,
  },
  {
    name: "Full Service",
    fee: "20%",
    description:
      "Complete hands-off management including cleaning, maintenance, and operations.",
    features: [
      "EVERYTHING IN TIER 1 PLUS:",
      "Cleaning & Turnover Management",
      "Routine Property Inspection",
      "Inventory Control & Restocking",
      "Noise & Party Monitoring",
      "Emergency Repair Coordination",
      "Compliance Monitoring",
      "Damage Claim Management",
      "Smart Lock Management",
      "Leak Detection Monitoring",
      "Advanced SEO Listing Optimization",
      "Monthly Owner Performance Reports",
      "Direct Booking Setup (Optional)",
      "Damage Claim Processing",
    ],
    highlighted: true,
  },
  {
    name: "Premium Plus",
    fee: "25%",
    description:
      "The ultimate white-glove service for investors scaling their portfolio.",
    features: [
      "EVERYTHING IN TIER 1 & TIER 2 PLUS:",
      "Full Operational Takeover",
      "Dedicated Account Manager",
      "Priority 24/7 Owner Support",
      "Custom Revenue Growth Plan",
      "Advanced AI Pricing Strategy",
      "Professional Photography Coordination",
      "Interior Optimization Consulting",
      "Quarterly Revenue Strategy Calls",
      "Multi-Property Expansion Planning",
      "Investment Performance Forecasting",
      "VIP Guest Experience Optimization",
    ],
    highlighted: false,
  },
];

export default function Pricing() {
  const scrollToForm = () => {
    const formElement = document.getElementById("lead-form");
    if (formElement) {
      window.dispatchEvent(new Event("force-load-form"));
      formElement.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      className="py-24 bg-background relative overflow-hidden"
      id="pricing"
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pricingSchema) }}
      />
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-foreground">
            Premium Management.{" "}
            <span className="text-primary italic">Proven Results.</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            We are not cheap co-hosting. We are elite property managers who know
            exactly how to maximize your ROI while protecting your asset. Choose
            the level of service that fits your investment goals.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto items-start">
          {tiers.map((tier, index) => (
            <div
              key={index}
              className={`relative rounded-2xl border ${
                tier.highlighted
                  ? "border-primary bg-card/50 shadow-2xl shadow-primary/10 md:scale-105 z-10"
                  : "border-border bg-card/30"
              } p-8 flex flex-col h-full`}
            >
              {tier.highlighted && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-sm font-bold px-4 py-1 rounded-full flex items-center gap-1">
                  <Star className="w-4 h-4 fill-current" /> Most Popular
                </div>
              )}

              <div className="mb-8">
                <h3 className="text-2xl font-bold text-foreground mb-2">
                  {tier.name}
                </h3>
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="text-5xl font-bold text-primary">
                    {tier.fee}
                  </span>
                </div>
                <p className="text-muted-foreground text-sm">
                  {tier.description}
                </p>
              </div>

              <div className="flex-grow">
                <ul className="space-y-4 mb-8">
                  {tier.features.map((feature, i) => {
                    const isHighlightRow = feature.includes("EVERYTHING IN");
                    return (
                      <li
                        key={i}
                        className={`flex items-start gap-3 ${isHighlightRow ? "font-bold text-foreground pt-4 border-t border-border/50" : "text-sm text-muted-foreground"}`}
                      >
                        {!isHighlightRow && (
                          <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                        )}
                        <span>{feature}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div className="mt-auto pt-8">
                <Button
                  onClick={scrollToForm}
                  variant={tier.highlighted ? "default" : "outline"}
                  className="w-full py-6 text-lg font-bold rounded-xl"
                >
                  Get Started
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
